/**
 * Host action dispatcher — the React Native counterpart of the web widget's
 * `executeDomAction`.
 *
 * Core normalises the `execute_dom_action` RPC into a `DomAction` and hands it
 * to the `onDomAction` port. On web that ends in `window.dispatchEvent`, which
 * works because the host page is already an event bus. RN has no such ambient
 * bus, so the integrator supplies the handler and gets the event name plus the
 * payload the agent sent — the same `{...default_payload, action_id}` detail
 * the web CustomEvent carries, so a shared backend config drives both.
 *
 * Validation, logging and the "unknown kind" path stay identical to web: those
 * are contract behaviour, not platform detail.
 */

import type { DomAction, DomActionLogEntry, DomActionLogger } from "@ringg/core";

/** What the host receives — the web CustomEvent, minus the DOM. */
export interface HostAction {
  /** The agent-configured event name (`CustomEvent.type` on web). */
  name: string;
  /** `default_payload` with the action id folded in (`CustomEvent.detail`). */
  payload: Record<string, unknown>;
}

export type HostActionHandler = (action: HostAction) => void;

const warn = (...args: unknown[]): void => {
  console.warn("[HOST_ACTION]", ...args);
};

/**
 * Builds the `onDomAction` port. `onAction` runs in the caller's context and
 * is allowed to throw — a broken host handler is reported, never fatal.
 */
export const createHostActionDispatcher =
  (onAction: HostActionHandler) =>
  (action: DomAction | undefined | null, onLog?: DomActionLogger): void => {
    const emit = (entry: DomActionLogEntry): void => {
      try {
        onLog?.(entry);
      } catch (error) {
        warn("logger threw", error);
      }
    };

    if (!action || typeof action !== "object") {
      warn("Missing or invalid action config");
      emit({ label: "Invalid action", level: "error" });
      return;
    }

    const { id, kind } = action;

    if (kind !== "trigger_event") {
      warn(`[${id}] unknown kind`, kind);
      emit({ id, label: "Unknown kind", meta: String(kind), level: "error" });
      return;
    }

    if (!action.event_name) {
      warn(`[${id}] trigger_event missing event_name`);
      emit({ id, label: "Event missing name", level: "error" });
      return;
    }

    try {
      onAction({ name: action.event_name, payload: { ...(action.default_payload ?? {}), action_id: id } });
      emit({ id, label: "Triggered", meta: action.event_name, level: "info" });
    } catch (error) {
      warn(`[${id}] execution failed`, error);
      emit({ id, label: "Action failed", level: "error" });
    }
  };
